import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReplyIcon from "@mui/icons-material/Reply";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UndoIcon from "@mui/icons-material/Undo";
import SendIcon from "@mui/icons-material/Send";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";
import {
  PERMISSIONS,
  PQRSD_RESPONSE_FLOW_STATUS,
  PQRSD_STATUS,
  PQRSD_TYPES,
  PQRSD_PRIORITY_LABELS,
  PQRSD_PRIORITY_COLORS,
} from "../../constants";
import {
  assignPqrsd,
  getPqrsd,
  closePqrsd,
  setPqrsdInProgress,
  returnPqrsd,
  returnPqrsdToTriage,
  forwardPqrsd,
  reassignPqrsd,
  getPqrsdResponses,
  listInfoRequests,
  saveInfoRequest,
  sendInfoRequest,
  submitInfoRequestForReview,
  rejectInfoRequest,
  retractInfoRequest,
  deleteInfoRequest,
  resendInfoRequestLink,
  approveInfoRequest,
  withdrawInfoRequest,
  extendInfoRequest,
  decreeDesistimiento,
  resolveAppeal,
  PQRSD_ANNULLED_ERROR,
} from "../../services/backend/PqrsdService";
import { getReciversUser } from "../../services/backend/UserService";
import { getPqrsdConfig, DEFAULT_PQRSD_CONFIG, listPqrsdGroups } from "../../services/backend/PqrsdConfigService";
import { setCurrentPqrsd } from "../../redux/slices/pqrsdSlice";
import StartResponseFlowDialog from "./StartResponseFlowDialog";
import ResponseFlowProgress from "./ResponseFlowProgress";
import ReturnPqrsdDialog from "./ReturnPqrsdDialog";
import InfoRequestPanel from "./InfoRequestPanel";
import DesistimientoAppealPanel from "./DesistimientoAppealPanel";
import InfoRequestBuilderDialog from "./InfoRequestBuilderDialog";
import FiledCancellationNotice from "../filed/FiledCancellationNotice";
import ForwardPqrsdDialog from "./ForwardPqrsdDialog";
import ReassignPqrsdDialog from "./ReassignPqrsdDialog";
import PqrsdLogsDrawer from "./PqrsdLogsDrawer";
import PqrsdAiPanel from "./PqrsdAiPanel";
import RadicadoTimeline from "../filed/RadicadoTimeline";
import { getFiled } from "../../services/backend/CorrespondenceService";

import { pqrsdText } from "./pqrsdLabel";
import { DEFAULT_REOPEN_POLICY, normalizeReopenPolicy } from "../../services/backend/gfunctions/pqrsd/_shared/reopenPolicy";
export const STATUS_COLOR = {
  [PQRSD_STATUS.RADICADO]: "info",
  [PQRSD_STATUS.ASIGNADO]: "info",
  [PQRSD_STATUS.EN_TRAMITE]: "warning",
  [PQRSD_STATUS.RESPONDIDO]: "success",
  [PQRSD_STATUS.CERRADO]: "default",
  [PQRSD_STATUS.DEVUELTO]: "warning",
  [PQRSD_STATUS.REABIERTO]: "info",
  [PQRSD_STATUS.PENDIENTE_INFO]: "secondary",
  [PQRSD_STATUS.DESISTIDO]: "error",
  [PQRSD_STATUS.EN_RECURSO]: "warning",
  [PQRSD_STATUS.ANULADO]: "error",
};

export const STATUS_LABELS = {
  [PQRSD_STATUS.RADICADO]: "Radicado",
  [PQRSD_STATUS.ASIGNADO]: "Asignado",
  [PQRSD_STATUS.EN_TRAMITE]: "En trámite",
  [PQRSD_STATUS.RESPONDIDO]: "Respondido",
  [PQRSD_STATUS.CERRADO]: "Cerrado",
  [PQRSD_STATUS.DEVUELTO]: "Devuelta",
  [PQRSD_STATUS.REABIERTO]: "Reabierta",
  [PQRSD_STATUS.PENDIENTE_INFO]: "Pendiente de información",
  [PQRSD_STATUS.DESISTIDO]: "Desistida",
  [PQRSD_STATUS.EN_RECURSO]: "En recurso de reposición",
  [PQRSD_STATUS.ANULADO]: "Anulada",
};

function formatDateTime(v) {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

export default function PqrsdAdminDetail() {
  const { radicado } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);
  const pqrsdDoc = useSelector((state) => state.pqrsd.current);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const perms = user?.permissions || [];
  const canRead = perms.includes(PERMISSIONS.PQRSD.READ);
  const canStartResponseFlow =
    perms.includes(PERMISSIONS.PQRSD.START_RESPONSE_FLOW) ||
    perms.includes(PERMISSIONS.PQRSD.RESPOND);
  const canClose = perms.includes(PERMISSIONS.PQRSD.CLOSE);
  const canAssign = perms.includes(PERMISSIONS.PQRSD.ASSIGN);
  const canReturn = perms.includes(PERMISSIONS.PQRSD.RETURN);
  const canForward = perms.includes(PERMISSIONS.PQRSD.FORWARD);
  // Dependency-supervisor scoped powers: assign within their dependency and
  // bounce a misrouted PQRSD back to Triage.
  const canAssignGroup = perms.includes(PERMISSIONS.PQRSD.ASSIGN_GROUP);
  const canReturnTriage = perms.includes(PERMISSIONS.PQRSD.RETURN_TRIAGE);
  // PQRSD.LIST is held by ADMIN and PQRSD_SUPERVISOR but not operators — the raw
  // event log is restricted to those roles.
  const canViewLogs = perms.includes(PERMISSIONS.PQRSD.LIST);
  // Requerimiento de información al ciudadano (CPACA art. 17). Decreeing the
  // desistimiento archives the petition, so it stays supervisor/admin-only.
  const canRequestInfo = perms.includes(PERMISSIONS.PQRSD.REQUEST_INFO);
  // Entity-wide approval. A dependency lead also approves, but only on their
  // own dependency's PQRSDs — see pqrsdIsMine below.
  const canApproveResponse = perms.includes(PERMISSIONS.PQRSD.APPROVE_RESPONSE);
  const canDecree = perms.includes(PERMISSIONS.PQRSD.DECREE_DESISTIMIENTO);

  const [logsOpen, setLogsOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [internalUsers, setInternalUsers] = useState([]);
  const [assigneeChoice, setAssigneeChoice] = useState([]);
  const [startFlowOpen, setStartFlowOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnTriageOpen, setReturnTriageOpen] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  // Group ids the current user is a designated dependency lead of (drives the
  // scoped assign + return-to-triage actions). null = not yet resolved.
  const [mySupervisedGroupIds, setMySupervisedGroupIds] = useState([]);
  // Per-institution interim cap (config.maxInterimsPerCycle). 0 = unlimited.
  const [maxInterims, setMaxInterims] = useState(DEFAULT_PQRSD_CONFIG.maxInterimsPerCycle);
  // Per-institution reopen cap (config.maxReopens). 0 = unlimited.
  const [maxReopens, setMaxReopens] = useState(DEFAULT_REOPEN_POLICY.maxReopens);
  const [pqrsdConfig, setPqrsdConfig] = useState(DEFAULT_PQRSD_CONFIG);
  const [infoRequests, setInfoRequests] = useState([]);
  // The requerimiento currently open in the builder: null for a new one, the
  // stored doc when someone is reworking a draft or a review.
  const [editingRequest, setEditingRequest] = useState(null);

  const [infoBusy, setInfoBusy] = useState(false);
  const [infoError, setInfoError] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);

  useEffect(() => {
    if (!canRead) {
      navigate("/home");
      return;
    }
    let active = true;
    (async () => {
      setLoading(true);
      const data = await getPqrsd(decodeURIComponent(radicado));
      if (!active) return;
      dispatch(setCurrentPqrsd(data));
      setLoading(false);
    })();
    return () => { active = false; };
  }, [canRead, dispatch, navigate, radicado]);

  useEffect(() => {
    let active = true;
    (async () => {
      const cfg = await getPqrsdConfig();
      if (!active) return;
      setPqrsdConfig(cfg || DEFAULT_PQRSD_CONFIG);
      setMaxInterims(cfg?.maxInterimsPerCycle ?? DEFAULT_PQRSD_CONFIG.maxInterimsPerCycle);
      setMaxReopens(normalizeReopenPolicy(cfg).maxReopens);
    })();
    return () => { active = false; };
  }, []);

  // Resolve the dependencies the current user leads, but only when they hold a
  // scoped (non-supervisor) power — a full supervisor gates on canAssign instead.
  // Leading the PQRSD's dependency is also what lets a drafter's lead approve
  // its requerimientos.
  useEffect(() => {
    if (!(canAssignGroup || canReturnTriage || canRequestInfo) || !user?.userId) return;
    let active = true;
    (async () => {
      const groups = await listPqrsdGroups();
      const ids = (groups || [])
        .filter((g) => Array.isArray(g.supervisorIds) && g.supervisorIds.includes(user.userId))
        .map((g) => g.id);
      if (active) setMySupervisedGroupIds(ids);
    })();
    return () => { active = false; };
  }, [canAssignGroup, canReturnTriage, canRequestInfo, user?.userId]);

  const pqrsd = pqrsdDoc?.pqrsd || {};

  // Declared here and not with the other state: it reads `pqrsd`, which is
  // derived below the hooks. 0 means unlimited, the server's convention.
  const maxInfoRequests = Number(
    pqrsdConfig?.maxInfoRequestsPerCycle ?? DEFAULT_PQRSD_CONFIG.maxInfoRequestsPerCycle
  );
  const infoRequestsExhausted =
    maxInfoRequests > 0 && (pqrsd?.infoRequestCount || 0) >= maxInfoRequests;

  // True when this PQRSD is routed to a dependency the current user leads.
  const pqrsdIsMine = !!pqrsd.groupId && mySupervisedGroupIds.includes(pqrsd.groupId);

  // Supervisors (assign/list) may override the AI classification & routing.
  const canOverrideAi = canAssign || canViewLogs;

  // Re-fetch the PQRSD after a mutation so the UI reflects the latest server state.
  const refresh = async () => {
    const fresh = await getPqrsd(pqrsdDoc.filedId);
    dispatch(setCurrentPqrsd(fresh));
  };

  // A refusal because someone annulled the PQRSD while this page was open: say
  // so, and reload it so the stale buttons give way to the annulment notice.
  const reportError = (e) => {
    setError(e?.message || "No fue posible completar la acción.");
    // Thrown relabeled (PqrsdService), so compared relabeled.
    if (e?.message === pqrsdText(PQRSD_ANNULLED_ERROR)) refresh().catch(() => {});
  };

  // The current user owns this PQRSD if they're among the (possibly multiple)
  // assignees. Falls back to the legacy single `assignee` field for old docs.
  const isAssignee = Array.isArray(pqrsd.assigneeIds)
    ? pqrsd.assigneeIds.includes(user?.userId)
    : pqrsd.assignee === user?.userId;
  // Normalized list of assignees for display (legacy docs expose only assignee).
  const assigneeList = Array.isArray(pqrsd.assignees) && pqrsd.assignees.length
    ? pqrsd.assignees
    : pqrsd.assignee
      ? [{ userId: pqrsd.assignee, name: pqrsd.assigneeName, email: pqrsd.assigneeEmail }]
      : [];

  const handleRespond = () => {
    setStartFlowOpen(true);
  };

  const handleFlowStarted = ({ flowId }) => {
    setStartFlowOpen(false);
    navigate(`/flow/${flowId}`);
  };

  // Open the read-only Filed View for a response. responses[] only store the
  // filedId, so resolve the referenceId (the /filed/view hash) first; fall back
  // to the editable form route if it can't be resolved.
  const handleViewResponse = async (responseFiledId) => {
    if (!responseFiledId) return;
    const filed = await getFiled(user?.token, responseFiledId);
    const ref = filed?.referenceId;
    if (ref) navigate(`/filed/view/${encodeURIComponent(ref)}`);
    else navigate(`/filed/${responseFiledId}`);
  };

  const handleTakeInProgress = async () => {
    setBusy(true);
    setError(null);
    try {
      await setPqrsdInProgress(pqrsdDoc.filedId);
      const fresh = await getPqrsd(pqrsdDoc.filedId);
      dispatch(setCurrentPqrsd(fresh));
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };

  // The roster the assign dialog and the requerimiento reviewer picker both
  // choose from. A dependency supervisor (scoped assign, no full ASSIGN) may
  // only pick members of the dependency this PQRSD is routed to — load that
  // group instead of every internal user. Loaded fresh when scoped, since it
  // varies per PQRSD's groupId.
  const loadInternalUsers = async () => {
    if (!canAssign && canAssignGroup) {
      const groups = await listPqrsdGroups();
      const group = (groups || []).find((g) => g.id === pqrsd.groupId);
      setInternalUsers(Array.isArray(group?.members) ? group.members : []);
      return;
    }
    if (internalUsers.length === 0 && user?.token) {
      const res = await getReciversUser(user.token);
      // The handleUser INTERNAL_RECEIVER endpoint returns { result: true, users: [...] } in some
      // versions and an array in others — normalize defensively.
      const list = Array.isArray(res) ? res : res?.users || res?.result || [];
      setInternalUsers(Array.isArray(list) ? list : []);
    }
  };

  const handleOpenAssign = async () => {
    setAssignOpen(true);
    await loadInternalUsers();
  };

  const handleConfirmAssign = async () => {
    if (!assigneeChoice.length) return;
    setBusy(true);
    setError(null);
    try {
      await assignPqrsd(
        pqrsdDoc.filedId,
        assigneeChoice.map((a) => ({
          userId: a.userId,
          name: `${a.name || ""} ${a.lastName || ""}`.trim(),
          email: a.email,
        })),
        user
      );
      const fresh = await getPqrsd(pqrsdDoc.filedId);
      dispatch(setCurrentPqrsd(fresh));
      setAssignOpen(false);
      setAssigneeChoice([]);
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleClose = async () => {
    if (!window.confirm(pqrsdText("¿Cerrar esta PQRSD?"))) return;
    setBusy(true);
    setError(null);
    try {
      await closePqrsd(pqrsdDoc.filedId);
      const fresh = await getPqrsd(pqrsdDoc.filedId);
      dispatch(setCurrentPqrsd(fresh));
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleReturn = async (reason) => {
    setBusy(true);
    setError(null);
    try {
      await returnPqrsd(pqrsdDoc.filedId, reason, user);
      const fresh = await getPqrsd(pqrsdDoc.filedId);
      dispatch(setCurrentPqrsd(fresh));
      setReturnOpen(false);
    } catch (e) {
      reportError(e);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const handleReturnToTriage = async (reason) => {
    setBusy(true);
    setError(null);
    try {
      await returnPqrsdToTriage(pqrsdDoc.filedId, reason, user);
      const fresh = await getPqrsd(pqrsdDoc.filedId);
      dispatch(setCurrentPqrsd(fresh));
      setReturnTriageOpen(false);
    } catch (e) {
      reportError(e);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const handleForward = async ({ toUser, note }) => {
    setBusy(true);
    setError(null);
    try {
      await forwardPqrsd(pqrsdDoc.filedId, toUser, note, user);
      const fresh = await getPqrsd(pqrsdDoc.filedId);
      dispatch(setCurrentPqrsd(fresh));
      setForwardOpen(false);
    } catch (e) {
      reportError(e);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  // --- Requerimiento de información (CPACA art. 17) ------------------------

  const refreshInfoRequests = useCallback(async () => {
    if (!canRequestInfo && !canViewLogs) return;
    const list = await listInfoRequests(decodeURIComponent(radicado));
    setInfoRequests(list);
  }, [canRequestInfo, canViewLogs, radicado]);

  useEffect(() => { refreshInfoRequests(); }, [refreshInfoRequests]);

  // Every requerimiento action mutates the parent PQRSD too (status, SLA), so
  // both the doc and the subcollection are re-read afterwards.
  const runInfoAction = async (fn) => {
    setInfoBusy(true);
    setInfoError(null);
    try {
      const out = await fn();
      const fresh = await getPqrsd(decodeURIComponent(radicado));
      dispatch(setCurrentPqrsd(fresh));
      await refreshInfoRequests();
      return out;
    } catch (e) {
      setInfoError(e.message);
      throw e;
    } finally {
      setInfoBusy(false);
    }
  };

  const handleSendInfoRequest = async ({ requestId, items, note, reviewerIds }) => {
    const out = await runInfoAction(() =>
      sendInfoRequest(decodeURIComponent(radicado), { requestId, items, note, reviewerIds }, user)
    );
    setBuilderOpen(false);
    setEditingRequest(null);
    return out;
  };

  // Draft / review: several officers may shape the same requerimiento before it
  // leaves the entity, so saving is a first-class action and not only a step on
  // the way to sending.
  const handleSaveInfoRequest = async ({ requestId, items, note }) => {
    const out = await runInfoAction(() =>
      saveInfoRequest(decodeURIComponent(radicado), { requestId, items, note }, user)
    );
    setBuilderOpen(false);
    setEditingRequest(null);
    return out;
  };

  const handleSubmitInfoRequestForReview = async ({ requestId, items, note, reviewerIds }) => {
    const out = await runInfoAction(async () => {
      // Save whatever is on screen first: the reviewers must be mailed the
      // version the drafter is looking at, not the last one persisted.
      const saved = await saveInfoRequest(
        decodeURIComponent(radicado), { requestId, items, note }, user
      );
      return submitInfoRequestForReview(
        decodeURIComponent(radicado), saved.requestId, user, { reviewerIds }
      );
    });
    setBuilderOpen(false);
    setEditingRequest(null);
    return out;
  };

  const handleSubmitExistingForReview = (requestId) =>
    runInfoAction(() => submitInfoRequestForReview(decodeURIComponent(radicado), requestId, user))
      .catch(() => {});

  const handleRejectInfoRequest = (requestId, comment) =>
    runInfoAction(() => rejectInfoRequest(decodeURIComponent(radicado), requestId, comment, user));

  const handleRetractInfoRequest = (requestId) =>
    runInfoAction(() => retractInfoRequest(decodeURIComponent(radicado), requestId, user)).catch(() => {});

  const handleDeleteInfoRequest = (requestId) =>
    runInfoAction(() => deleteInfoRequest(decodeURIComponent(radicado), requestId, user)).catch(() => {});

  const handleEditInfoRequest = (request) => {
    setEditingRequest(request);
    setBuilderOpen(true);
  };

  const handleApproveInfoRequest = (requestId) =>
    runInfoAction(() => approveInfoRequest(decodeURIComponent(radicado), requestId, user)).catch(() => {});

  const handleWithdrawInfoRequest = (requestId, reason) =>
    runInfoAction(() => withdrawInfoRequest(decodeURIComponent(radicado), requestId, reason, user));

  const handleExtendInfoRequest = (requestId) =>
    runInfoAction(() => extendInfoRequest(decodeURIComponent(radicado), requestId, user)).catch(() => {});

  // Copy/resend does not mutate the PQRSD, so it skips the refresh cycle.
  const handleResendLink = (requestId, { sendEmail } = {}) =>
    resendInfoRequestLink(decodeURIComponent(radicado), requestId, user, { sendEmail });

  const handleDecreeDesistimiento = (reason) =>
    runInfoAction(() => decreeDesistimiento(decodeURIComponent(radicado), reason, user));

  const handleResolveAppeal = (decision, reason) =>
    runInfoAction(() => resolveAppeal(decodeURIComponent(radicado), decision, reason, user));

  const handleReassign = async ({ toUsers }) => {
    setBusy(true);
    setError(null);
    try {
      await reassignPqrsd(pqrsdDoc.filedId, toUsers, user);
      const fresh = await getPqrsd(pqrsdDoc.filedId);
      dispatch(setCurrentPqrsd(fresh));
      setReassignOpen(false);
    } catch (e) {
      reportError(e);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  // Active response flows. Several may be in progress at once; fall back to the
  // legacy single pointer for PQRSDs created before the multi-flow model.
  const activeFlows = Array.isArray(pqrsd.activeResponseFlows) && pqrsd.activeResponseFlows.length
    ? pqrsd.activeResponseFlows
    : pqrsd.responseFlowId
      ? [{ flowId: pqrsd.responseFlowId, status: pqrsd.currentResponseFlowStatus || pqrsd.responseFlowStatus, responseType: pqrsd.currentResponseType }]
      : [];
  // An entry stays in the active set (status "in_progress") until its flow
  // completes or is cancelled — a reviewer-rejected draft still counts as active.
  const anyFlowInProgress = activeFlows.some((f) => f.status === "in_progress");
  const isAnnulled = pqrsd.status === PQRSD_STATUS.ANULADO;

  // Compute close-button gating: block while any response flow is still being
  // drafted/approved, while the citizen flagged needs_more_info on the latest
  // final response, or while the case has been reopened. A cycle is finished
  // once a final response is on file (or the status already says respondido).
  const lastResponse = (pqrsd.responses || []).slice(-1)[0] || null;
  const cycleFinished =
    pqrsd.status === PQRSD_STATUS.RESPONDIDO
    || (pqrsd.responses || []).some((r) => r.responseType === "final");
  const closeBlockReason = (() => {
    if (pqrsd.status === PQRSD_STATUS.CERRADO) return pqrsdText("Esta PQRSD ya está cerrada.");
    if (anyFlowInProgress) {
      return "Hay una o más respuestas en redacción o aprobación. Termina o cancela los flujos antes de cerrar.";
    }
    if (lastResponse?.responseType === "final" && lastResponse?.citizenAction === "needs_more_info") {
      return "El ciudadano solicitó más información. Atiéndela antes de cerrar.";
    }
    if (pqrsd.status === PQRSD_STATUS.REABIERTO) {
      return pqrsdText("La PQRSD fue reabierta por el ciudadano. Atiende la solicitud antes de cerrar.");
    }
    if (pqrsd.status === PQRSD_STATUS.PENDIENTE_INFO) {
      return "Hay un requerimiento de información pendiente. Espera la respuesta del ciudadano, retíralo o decreta el desistimiento.";
    }
    if (!cycleFinished) {
      return "Solo se puede cerrar después de una respuesta final.";
    }
    return null;
  })();

  // Anonymous mask: hide identifying solicitor fields from staff when the
  // citizen requested anonymity. Email is also masked here even though it
  // exists on the doc, because internal users shouldn't see who submitted.
  const showSolicitor = !pqrsd.anonymous;
  const responses = getPqrsdResponses(pqrsdDoc);
  const interimCount = pqrsd.interimCountThisCycle || 0;
  // Interim cap also counts interim drafts still in flight (not yet filed).
  const inFlightInterims = activeFlows.filter(
    (f) => f.responseType === "interim" && f.status === "in_progress"
  ).length;
  const effectiveInterimCount = interimCount + inFlightInterims;

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!pqrsdDoc) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning">{pqrsdText("No se encontró esta PQRSD.")}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Radicado</Typography>
          <Typography variant="h4" sx={{ fontFamily: "monospace" }}>{pqrsdDoc.filedId}</Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
            {pqrsdDoc.subject}
          </Typography>
        </Box>
        <Chip
          label={STATUS_LABELS[pqrsd.status] || pqrsd.status}
          color={STATUS_COLOR[pqrsd.status] || "default"}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Estado previo is the radicado's (Finalizado), which reads wrong on a PQRSD. */}
      {isAnnulled && (
        <FiledCancellationNotice filed={pqrsdDoc} title={pqrsdText("PQRSD ANULADA")} linkToFiled showPreviousStatus={false} />
      )}

      <PqrsdAiPanel pqrsdDoc={pqrsdDoc} canOverride={canOverrideAi && !isAnnulled} annulled={isAnnulled} onChanged={refresh} />

      {pqrsd.responseFlowStatus === PQRSD_RESPONSE_FLOW_STATUS.REJECTED && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          La respuesta fue devuelta por un revisor. Reanuda la redacción para
          atender las observaciones y volver a someterla a aprobación.
        </Alert>
      )}

      {pqrsd.status === PQRSD_STATUS.DEVUELTO && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{pqrsdText("PQRSD devuelta por el operario.")}</strong>{" "}
          {pqrsd.returnedReason || "Reasígnala a otro funcionario."}
        </Alert>
      )}

      {pqrsd.status === PQRSD_STATUS.REABIERTO && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>
            {pqrsdText("El ciudadano reabrió la PQRSD")}
            {pqrsd.reopenedReasonLabel ? `: ${pqrsd.reopenedReasonLabel}` : ""}.
          </strong>{" "}
          <span style={{ whiteSpace: "pre-wrap" }}>{pqrsd.reopenedReason || "—"}</span>
        </Alert>
      )}

      {pqrsd.status === PQRSD_STATUS.CERRADO && pqrsd.citizenRating?.label && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Calificación del ciudadano:</strong> {pqrsd.citizenRating.label}
          {pqrsd.citizenRating.score ? ` (${pqrsd.citizenRating.score}/5)` : ""}
        </Alert>
      )}

      {Number(maxInterims) !== 0 && effectiveInterimCount >= maxInterims && pqrsd.status !== PQRSD_STATUS.RESPONDIDO && pqrsd.status !== PQRSD_STATUS.CERRADO && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Se alcanzó el límite de respuestas interinas en este ciclo. La próxima respuesta debe ser final.
        </Alert>
      )}

      {cycleFinished && pqrsd.status !== PQRSD_STATUS.CERRADO && (
        <Alert severity="success" sx={{ mb: 2 }}>
          La respuesta fue aprobada y radicada. Puedes <strong>{pqrsdText("cerrar la PQRSD")}</strong> o iniciar una <strong>nueva respuesta</strong> si necesitas complementar.
        </Alert>
      )}

      {activeFlows.map((f) => (
        <ResponseFlowProgress key={f.flowId} flowId={f.flowId} />
      ))}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Tipo</Typography>
            <Typography>{PQRSD_TYPES[pqrsd.type] || pqrsd.type || "—"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Canal</Typography>
            <Typography>{pqrsd.channel || "web"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Prioridad</Typography>
            {pqrsd.priority ? (
              <Chip
                size="small"
                color={PQRSD_PRIORITY_COLORS[pqrsd.priority] || "default"}
                label={PQRSD_PRIORITY_LABELS[pqrsd.priority] || pqrsd.priority}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography>—</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Dependencia</Typography>
            <Typography>{pqrsd.groupName || "—"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Recibida</Typography>
            <Typography>{formatDateTime(pqrsdDoc.createdAt)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Fecha límite</Typography>
            <Typography>
              {formatDateTime(pqrsd.slaDeadline)}
              {/* A void PQRSD has no term left to miss. */}
              {pqrsd.slaBreached && !pqrsd.slaPaused && !isAnnulled && (
                <Chip label="Vencida" color="error" size="small" sx={{ ml: 1 }} />
              )}
              {/* Suspended by a requerimiento de información: the printed date
                  is the one the term had when it stopped, not a live deadline. */}
              {pqrsd.slaPaused && (
                <Tooltip title={`Suspendido por un requerimiento de información. Quedaban ${pqrsd.slaRemainingBusinessDays ?? "—"} días hábiles.`}>
                  <Chip label="Suspendido" color="secondary" size="small" variant="outlined" sx={{ ml: 1 }} />
                </Tooltip>
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Asignado a</Typography>
            {assigneeList.length ? (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {assigneeList.map((a) => (
                  <Chip key={a.userId} size="small" label={a.name || a.email || a.userId} />
                ))}
              </Stack>
            ) : (
              <Typography>—</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Solicitante</Typography>
            <Typography>
              {showSolicitor
                ? `${pqrsd.solicitor?.name || ""} ${pqrsd.solicitor?.surname || ""}`.trim() || "—"
                : "Ciudadano anónimo"}
            </Typography>
            {showSolicitor && (
              <Typography variant="body2" color="text.secondary">
                {pqrsd.solicitor?.idType} {pqrsd.solicitor?.idNumber}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Correo</Typography>
            <Typography>
              {showSolicitor
                ? (pqrsd.email || "—")
                // Masking an address that does not exist would suggest one is on file.
                : pqrsd.email ? "•••••••• (anónimo)" : <em>Sin correo (anónimo): no recibe notificaciones</em>}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Descripción</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{pqrsd.body || "—"}</Typography>
          </Grid>
          {Array.isArray(pqrsdDoc.annexes) && pqrsdDoc.annexes.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Anexos</Typography>
              <Stack spacing={1}>
                {pqrsdDoc.annexes.map((a, i) => (
                  <Button
                    key={i}
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ justifyContent: "flex-start", textTransform: "none" }}
                  >
                    {a.name}
                  </Button>
                ))}
              </Stack>
            </Grid>
          )}
          {pqrsdDoc.fileUrl && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                href={pqrsdDoc.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Descargar PDF radicado
              </Button>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* An annulled PQRSD is void: nothing left to assign, answer or close. */}
        {!isAnnulled && (
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {(canAssign || (canAssignGroup && pqrsdIsMine)) && pqrsd.status === PQRSD_STATUS.RADICADO && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenAssign}
              disabled={busy}
            >
              Asignar
            </Button>
          )}

          {canAssign && pqrsd.status === PQRSD_STATUS.DEVUELTO && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PersonAddIcon />}
              onClick={() => setReassignOpen(true)}
              disabled={busy}
            >
              Reasignar
            </Button>
          )}

          {/* Concurrent drafts allowed: the operator can always start another
              response regardless of any in-progress flows. Each existing flow is
              resumed from its own progress card ("Ver flujo"). The interim cap is
              enforced server-side and in the dialog. */}
          {canStartResponseFlow
            && [
              PQRSD_STATUS.ASIGNADO,
              PQRSD_STATUS.EN_TRAMITE,
              PQRSD_STATUS.REABIERTO,
              PQRSD_STATUS.RESPONDIDO,
            ].includes(pqrsd.status) && (
            <Button
              variant="contained"
              startIcon={<ReplyIcon />}
              onClick={handleRespond}
              disabled={busy}
            >
              {activeFlows.length > 0 ? "Otra respuesta" : "Responder"}
            </Button>
          )}

          {(pqrsd.status === PQRSD_STATUS.RADICADO || pqrsd.status === PQRSD_STATUS.ASIGNADO) && (
            <Button variant="outlined" onClick={handleTakeInProgress} disabled={busy}>
              Marcar en trámite
            </Button>
          )}

          {canRequestInfo
            && [PQRSD_STATUS.ASIGNADO, PQRSD_STATUS.EN_TRAMITE, PQRSD_STATUS.REABIERTO].includes(pqrsd.status) && (
            // Offered even once the cap was spent, so the officer wrote the
            // whole requerimiento and only learned on submit that the server
            // would refuse it. Kept visible but disabled, with the reason: the
            // norm contemplates a single requerimiento, and someone looking for
            // the button needs to know it is a rule, not a missing feature.
            <Tooltip
              title={
                infoRequestsExhausted
                  ? pqrsdText(`Ya se envió el máximo de requerimientos de información (${maxInfoRequests}) para esta PQRSD.`)
                  : ""
              }
            >
              <span>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<HelpOutlineIcon />}
              onClick={() => setBuilderOpen(true)}
              disabled={busy || infoBusy || infoRequestsExhausted}
            >
              Solicitar información
            </Button>
              </span>
            </Tooltip>
          )}

          {canReturn
            && isAssignee
            && [PQRSD_STATUS.ASIGNADO, PQRSD_STATUS.EN_TRAMITE, PQRSD_STATUS.REABIERTO].includes(pqrsd.status) && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<UndoIcon />}
              onClick={() => setReturnOpen(true)}
              disabled={busy}
            >
              Devolver
            </Button>
          )}

          {canForward
            && isAssignee
            && [PQRSD_STATUS.ASIGNADO, PQRSD_STATUS.EN_TRAMITE, PQRSD_STATUS.REABIERTO].includes(pqrsd.status) && (
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={() => setForwardOpen(true)}
              disabled={busy}
            >
              Reenviar
            </Button>
          )}

          {canReturnTriage && pqrsdIsMine && !anyFlowInProgress
            && [PQRSD_STATUS.RADICADO, PQRSD_STATUS.ASIGNADO, PQRSD_STATUS.EN_TRAMITE, PQRSD_STATUS.REABIERTO].includes(pqrsd.status) && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<UndoIcon />}
              onClick={() => setReturnTriageOpen(true)}
              disabled={busy}
            >
              Devolver a triage
            </Button>
          )}

          {canClose && pqrsd.status !== PQRSD_STATUS.CERRADO && (
            <Tooltip title={closeBlockReason || ""} disableHoverListener={!closeBlockReason}>
              <span>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={handleClose}
                  disabled={busy || !!closeBlockReason}
                >
                  {pqrsdText("Cerrar PQRSD")}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
        )}
      </Paper>

      {infoError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setInfoError(null)}>{infoError}</Alert>
      )}

      <InfoRequestPanel
        requests={infoRequests}
        pqrsd={pqrsd}
        canRequestInfo={canRequestInfo && !isAnnulled}
        canApprove={(canApproveResponse || pqrsdIsMine) && !isAnnulled}
        canDecree={canDecree && !isAnnulled}
        busy={infoBusy}
        onApprove={handleApproveInfoRequest}
        onWithdraw={handleWithdrawInfoRequest}
        onExtend={handleExtendInfoRequest}
        onDecree={handleDecreeDesistimiento}
        onResendLink={handleResendLink}
        onEdit={handleEditInfoRequest}
        onSubmitReview={handleSubmitExistingForReview}
        onReject={handleRejectInfoRequest}
        onDelete={handleDeleteInfoRequest}
        onRetract={handleRetractInfoRequest}
      />

      <DesistimientoAppealPanel
        pqrsd={pqrsd}
        canResolve={canDecree && !isAnnulled}
        busy={infoBusy}
        onResolve={handleResolveAppeal}
      />

      <InfoRequestBuilderDialog
        open={builderOpen}
        onClose={() => { setBuilderOpen(false); setEditingRequest(null); }}
        onSend={handleSendInfoRequest}
        onSaveDraft={handleSaveInfoRequest}
        onSubmitReview={handleSubmitInfoRequestForReview}
        request={editingRequest}
        busy={infoBusy}
        requiresApproval={!!pqrsdConfig.infoRequestRequiresApproval}
        citizenReplyDays={pqrsdConfig.citizenReplyDays ?? DEFAULT_PQRSD_CONFIG.citizenReplyDays}
        groupId={pqrsd.groupId || null}
      />

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{pqrsdText("Asignar PQRSD")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pqrsdText("Selecciona uno o varios funcionarios que se harán cargo de esta PQRSD. Cada uno recibirá una tarea en su bandeja.")}
          </Typography>
          <Autocomplete
            multiple
            options={internalUsers}
            getOptionLabel={(o) => `${o.name || ""} ${o.lastName || ""} <${o.email || ""}>`.trim()}
            isOptionEqualToValue={(o, v) => o?.userId === v?.userId}
            value={assigneeChoice}
            onChange={(_, v) => setAssigneeChoice(v)}
            renderInput={(params) => (
              <TextField {...params} label="Funcionarios" autoFocus />
            )}
            noOptionsText="Sin resultados"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)} disabled={busy}>Cancelar</Button>
          <Button onClick={handleConfirmAssign} variant="contained" disabled={!assigneeChoice.length || busy}>
            Confirmar asignación
          </Button>
        </DialogActions>
      </Dialog>

      {responses.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Respuestas</Typography>
          <Stack spacing={2}>
            {responses.slice().reverse().map((r) => (
              <Box key={r.responseFiledId || r.index}
                sx={{ borderLeft: 3, borderColor: r.responseType === "final" ? "success.main" : "warning.main", pl: 2 }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={r.responseType === "final" ? "Respuesta final" : "Respuesta interina"}
                    color={r.responseType === "final" ? "success" : "warning"}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {formatDateTime(r.filedAt)}
                  </Typography>
                  {r.responseFiledId && (
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      · {r.responseFiledId}
                    </Typography>
                  )}
                  {r.citizenAction === "confirmed" && (
                    <Chip size="small" label="Ciudadano conforme" color="success" variant="outlined" />
                  )}
                  {r.citizenAction === "needs_more_info" && (
                    <Chip size="small" label="Solicita más info" color="info" variant="outlined" />
                  )}
                  {r.responseFiledId && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => handleViewResponse(r.responseFiledId)}
                      sx={{ ml: "auto" }}
                    >
                      Ver respuesta
                    </Button>
                  )}
                </Stack>
                {r.citizenActionReason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                    "{r.citizenActionReason}"
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            Interinas en este ciclo: {interimCount}/2 · Reaperturas del ciudadano: {pqrsd.reopenCount || 0}
            {maxReopens === 0 ? " (sin límite)" : `/${maxReopens}`}
          </Typography>
        </Paper>
      )}

      <StartResponseFlowDialog
        open={startFlowOpen}
        onClose={() => setStartFlowOpen(false)}
        parentRadicadoId={pqrsdDoc.filedId}
        parentSubject={pqrsdDoc.subject}
        onStarted={handleFlowStarted}
        interimCountThisCycle={effectiveInterimCount}
        maxInterims={maxInterims}
      />

      <ReturnPqrsdDialog
        open={returnOpen}
        onClose={() => setReturnOpen(false)}
        onConfirm={handleReturn}
        busy={busy}
      />

      <ReturnPqrsdDialog
        open={returnTriageOpen}
        onClose={() => setReturnTriageOpen(false)}
        onConfirm={handleReturnToTriage}
        busy={busy}
        title="Devolver a triage"
        description={pqrsdText("Indica por qué esta PQRSD no corresponde a tu dependencia. Volverá a Triage para que el supervisor general la revise y la re-enrute a otra dependencia.")}
        reasonLabel="Motivo de la devolución a triage"
        confirmLabel="Devolver a triage"
        errorText={pqrsdText("No fue posible devolver la PQRSD a triage.")}
      />

      <ForwardPqrsdDialog
        open={forwardOpen}
        onClose={() => setForwardOpen(false)}
        onConfirm={handleForward}
        busy={busy}
        currentAssigneeId={pqrsd.assignee}
      />

      <ReassignPqrsdDialog
        open={reassignOpen}
        onClose={() => setReassignOpen(false)}
        onConfirm={handleReassign}
        busy={busy}
        previousReason={pqrsd.returnedReason}
      />

      {pqrsdDoc?.filedId && (
        <Accordion defaultExpanded sx={{ mt: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Trazabilidad</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <RadicadoTimeline
              rootRadicadoId={pqrsdDoc.filedId}
              currentRadicadoId={pqrsdDoc.filedId}
              currentRadicadoReferenceId={pqrsdDoc.referenceId}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {canViewLogs && (
        <>
          <Tooltip title="Ver logs del expediente" placement="left">
            <Button
              onClick={() => setLogsOpen(true)}
              variant="contained"
              size="small"
              sx={{
                position: "fixed",
                top: "40%",
                right: 0,
                zIndex: (theme) => theme.zIndex.drawer - 1,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                minWidth: 0,
                px: 0.75,
                py: 1.5,
              }}
            >
              <ReceiptLongIcon fontSize="small" sx={{ mr: 0.5 }} />
              Logs
            </Button>
          </Tooltip>
          <PqrsdLogsDrawer
            open={logsOpen}
            onClose={() => setLogsOpen(false)}
            events={pqrsdDoc?.events}
          />
        </>
      )}
    </Container>
  );
}
